import { useCallback, useState } from 'react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';

/**
 * Provides a way to take a one-off *snapshot* of the current editor document (ADF), on demand.
 *
 * This is deliberately NOT a live/subscribed value:
 * - The editor's live doc-change channel (`useDocChangeContext`) is only wired up inside the editor's
 *   render subtree; this can run outside it (in the Rovo config providers), so we read the document
 *   directly from the editor's global preset API instead.
 * - Reading is asynchronous under the hood: `requestDocument` serializes the ProseMirror doc to ADF on
 *   an idle frame (throttled via `requestIdleCallback`), so the snapshot lands in state on a later tick
 *   rather than being returned synchronously from `captureSnapshot()`.
 */
export const useEditorDocumentSnapshot = (): [JSONDocNode | null, () => void] => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const [documentSnapshot, setDocumentSnapshot] = useState<JSONDocNode | null>(null);

	const captureSnapshot = useCallback(() => {
		fullPageEditorPresetAPI?.core?.actions.requestDocument((doc) => {
			if (doc) {
				setDocumentSnapshot(doc);
			}
		});
	}, [fullPageEditorPresetAPI]);

	return [documentSnapshot, captureSnapshot];
};
